import { KeyValue } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ImpactedContractsCounts } from 'src/app/compliance/models/impacted-counts.model';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { AssignmentConfigurationSummary } from 'src/app/shared/models/assignment-configuration-summary.model';
import { ContractAssignmentSummary } from 'src/app/shared/models/contract-assignment-summary.model';
import { AssigneeRoleType } from 'src/app/shared/models/enums/assignee-role-type.enum';
import { Lookup } from 'src/app/shared/models/lookup.model';
import {
    BulkUnassignAssignmentChange,
    BulkUserAssignmentChange,
    UserAssignmentChange
} from 'src/app/shared/models/user-assignment-change-request.model';
import { interpolateParams } from 'src/app/shared/utilities/interpolate-params';
import { combine } from 'src/app/shared/utilities/route';
import { RoleUser } from '../lib/credentialing-shared/models/role-user';
import { AssignmentLevelType } from '../models/enums/assignee-level-type.enum';

@Injectable({
    providedIn: 'root'
})
export class ContractAssignmentService {
    private readonly _coreApiPath: string;

    constructor(@Inject(APP_CONFIG) private readonly _settings: Settings, private readonly _httpClient: HttpClient) {
        this._coreApiPath = combine(this._settings.coreAPIUrl, 'ayanova/contract-assignments');
    }

    getFacilityAssignmentByFacilityId(
        facilityId: number,
        assigneeRole: AssigneeRoleType
    ): Observable<AssignmentConfigurationSummary> {
        const params = interpolateParams({
            assigneeRole: assigneeRole
        });

        return this._httpClient.get<AssignmentConfigurationSummary>(
            `${this._coreApiPath}/configurations/facilities/${facilityId}`,
            { params }
        );
    }

    getQmAssignmentByAssocId(
        associationId: number,
        assigneeRole: AssigneeRoleType
    ): Observable<AssignmentConfigurationSummary> {
        const params = interpolateParams({
            assigneeRole: assigneeRole
        });

        return this._httpClient.get<AssignmentConfigurationSummary>(
            `${this._coreApiPath}/configurations/qm-associations/${associationId}`,
            { params }
        );
    }

    getContractAssignmentByContractId(
        contractId: number,
        assigneeRole?: AssigneeRoleType
    ): Observable<ContractAssignmentSummary[]> {
        const params = interpolateParams({
            assigneeRole: assigneeRole
        });

        return this._httpClient.get<ContractAssignmentSummary[]>(`${this._coreApiPath}/${contractId}`, { params });
    }

    getCandidateAssignmentByCandidateId(
        candidateId: number,
        assigneeRole?: AssigneeRoleType
    ): Observable<AssignmentConfigurationSummary> {
        const params = interpolateParams({
            assigneeRole: assigneeRole
        });

        return this._httpClient.get<AssignmentConfigurationSummary>(
            `${this._coreApiPath}/configurations/candidates/${candidateId}`,
            { params }
        );
    }

    updateCandidateAssignmentConfiguration(
        candidateId: number,
        assignmentChange: UserAssignmentChange
    ): Observable<KeyValue<string, unknown>[]> {
        return this._httpClient.post<KeyValue<string, unknown>[]>(
            `${this._coreApiPath}/configurations/candidates/${candidateId}`,
            assignmentChange
        );
    }

    updateContractAssignmentConfiguration(
        contractId: number,
        assignmentChange: UserAssignmentChange
    ): Observable<unknown> {
        return this._httpClient.post<unknown>(
            `${this._coreApiPath}/configurations/contracts/${contractId}`,
            assignmentChange
        );
    }

    updateBulkAssignmentConfiguration(assignments: BulkUserAssignmentChange): Observable<unknown[]> {
        let uri = '';
        switch (assignments.assignmentLevel) {
            case AssignmentLevelType.Contract:
                uri = `${this._coreApiPath}/configurations/contracts`;
                break;
            case AssignmentLevelType.Candidate:
                uri = `${this._coreApiPath}/configurations/candidates`;
                break;
            case AssignmentLevelType.Facility:
                uri = `${this._coreApiPath}/configurations/facilities`;
                break;
            case AssignmentLevelType.QMAssociation:
                uri = `${this._coreApiPath}/configurations/qm-associations`;
                break;
            default:
                throw new Error(`${assignments.assignmentLevel} bulk assignments not supported`);
        }

        return this._httpClient.post<unknown[]>(uri, assignments);
    }

    updateExpireAssignmentConfiguration(assignments: BulkUnassignAssignmentChange): Observable<unknown> {
        let uri = '';
        switch (assignments.assignmentLevel) {
            case AssignmentLevelType.Contract:
                uri = `${this._coreApiPath}/configurations/contracts/unassign`;
                break;
            case AssignmentLevelType.Candidate:
                uri = `${this._coreApiPath}/configurations/candidates/unassign`;
                break;
            case AssignmentLevelType.Facility:
                uri = `${this._coreApiPath}/configurations/facilities/unassign`;
                break;
            case AssignmentLevelType.QMAssociation:
                uri = `${this._coreApiPath}/configurations/qm-associations/unassign`;
                break;
            default:
                throw new Error(`${assignments.assignmentLevel} bulk expire not supported`);
        }

        return this._httpClient.post<unknown>(uri, assignments);
    }

    updateFacilityAssignmentConfiguration(
        facilityId: number,
        assignmentChange: UserAssignmentChange
    ): Observable<unknown> {
        return this._httpClient.post<unknown>(
            `${this._coreApiPath}/configurations/facilities/${facilityId}`,
            assignmentChange
        );
    }

    updateQmAssociationAssignmentConfiguration(
        associationId: number,
        assignment: UserAssignmentChange
    ): Observable<unknown> {
        return this._httpClient.post<unknown>(
            `${this._coreApiPath}/configurations/qm-associations/${associationId}`,
            assignment
        );
    }

    getCredentialingSpecialists(): Observable<Lookup[]> {
        const url = combine(this._coreApiPath, `credentialing-specialists`);
        return this._httpClient
            .get<Lookup[]>(url)
            .pipe(
                map((specialists) =>
                    specialists.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()))
                )
            );
    }

    getCredentialingSpecialistRoleUsers(): Observable<RoleUser[]> {
        return this._httpClient.get<RoleUser[]>(`${this._coreApiPath}/credentialing-specialists-role-users`);
    }

    getImpactedContractsCounts(
        ids: number[],
        assignmentLevelType: AssignmentLevelType
    ): Observable<ImpactedContractsCounts> {
        const assignmentLevel = AssignmentLevelType[assignmentLevelType].toLowerCase();
        const queryParams = ids.reduce(
            (queryParam, currentId, index) => `${queryParam}${assignmentLevel}Ids[${index}]=${currentId}&`,
            ''
        );

        switch (assignmentLevelType) {
            case AssignmentLevelType.QMAssociation: {
                return this._httpClient.get<ImpactedContractsCounts>(
                    `${this._coreApiPath}/configurations/qm-associations/contract-counts?${queryParams}`
                );
            }
            case AssignmentLevelType.Facility: {
                return this._httpClient.get<ImpactedContractsCounts>(
                    `${this._coreApiPath}/configurations/facilities/contract-counts?${queryParams}`
                );
            }
            case AssignmentLevelType.Candidate: {
                return this._httpClient.get<ImpactedContractsCounts>(
                    `${this._coreApiPath}/configurations/candidates/contract-counts?${queryParams}`
                );
            }
            default: {
                return null;
            }
        }
    }
}
